import { createReducer, on } from "@ngrx/store";
import { CheckActions } from "../../create-request/actions";
import { StandingAuthCheckActions, StandingAuthConfirmationPageActions, StandingAuthFormActions, StandingAuthReviewPageActions } from "../actions";
import { StandingAuthCheckInfo } from "../models/StandingAuthCheckInfo"
import { mapCheckReasonsToList } from "../../create-request/reducers/check.reducers";
import { StwProcessCode } from "../../shared/utilities/Constants";
import { DistributionReason } from "../../create-request/models/internal-transfer/DistributionReason";
import { CheckStandingAuthDetails } from "../models/standingAuthorizationForm";
import {InitReleaseActions} from "../../initiate-and-release/actions";
import { DraftListAttributes } from "../../initiate-and-release/Models/Drafts/DraftResponse";


export const standingAuthCheckInfoKey = 'checkInfo';

export interface State extends StandingAuthCheckInfo { }

export const initialStandingAuthCheckInfoState: State = {
    defaultPayee: [],
    loading: false,
    error: '', //when the API doesn't return a 200
    hasError: false,
    inputCallSucceeded: false,
    inputCallFinished: false,
    verifyCallFinished: false,
    inquiryCallFinished: false,
    initReleaseByDraftIdSucceeded: false,
    stateList: [],
    countryList: [],
    checkReason: '',
    checkReasonMap: [],
    defaultRecipient: [],
    isAlternateInvestmentUsage: false,
    alternateInvestmentText: '',
    accountPlatformCode: '',
    isPendingForCTWE: false,
    isPendingOnlyForCTWE: false,
    deliveryAddress: [],
    recipient: {
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        city: '',
        regionName: '',
        stateName: '',
        stateCode: '',
        countryCode: '',
        countryName: '',
        zipCode: '',
        zipSuffCode: '',
    },
    isFirstPartyModified: false,
    payeeDetails: {
        payeeName1: '',
        payeeName2: '',
        payeeName3: '',
        customerId1: 0,
        customerId2: 0,
        customerId3: 0,
    },
    type: '',
    useConstructedPayee: false,
    processDateMM: '',
    processDateDD: '',
    processDateYYYY: '',
    distributionReason: {
        code: '',
        subCode: '',
        description: '',
        isTaxExempt: false,
    },
    distributionReasonsList: [],
    fedTaxRate: '',
    defaultFedTaxRate: '',
    defaultStateTaxRate: '',
    fedTaxAmount: '',
    stateTaxRate: '',
    stateTaxAmount: '',
    nraAccountIndicator: false,
    stwProcessCode: '',
    fedTaxAmountFormatted: '',
    stateTaxAmountFormatted: '',
    pendReasons: [],
    totalWithdrawalAmount: '',
    stateTypeIndicator: '',
    stateCd: '',
    isNonNaState: false,
    deliveryMethodCode: '',
    checkFeeDetails: {
        defaultFee: 0,
        feeWaiverCode: '',
        qualFee: 0
    },
    serviceError: '', //errors that happen even when the API returns a 200 (see messageInfo field in verify response)
    shouldShowStateTax: false,
    displayClientAuthorizationSection: false,
    clientAuthorizationNeeded: false,
    eAuthModel: {
        isTransactionFromSLOA: false,
        eauthSelected: false,
        isTransactionFromSLOAOrSLOA: false,
        numberOfAuthRequired: 0,
        eauthEligibilityServiceSLOAError: false,
        eauthEligibilityServiceDown: false,
        eauthEligibilityServiceSLOAErrorMsg: '',
        isOrgAccount: false,
        accountRestricted: false,
        orgOrEduSavingsAccount: false,
        custAcctAuthorizationsListEmpty: false,
        eauthInd: false,
        eauthSLOAInd: false,
        allAccountHolderIneligible: false,
        numOfIneligibleCust: 0,
        eauthEligibilityServiceDownErrorMsg: '',
        custAcctAuthorizationsList: {
            allAccountHoldersIneligible: false,
            numOfIneligibleCust: 0,
            numOfNotWebEnabledCust: 0,
            totalNumOfAccountHolders: 0,
            eauthInd: false,
            eAuthCustomerList: [],
        },
        custAcctSLOAAuthorizationsList: {
            allAccountHoldersIneligible: false,
            numOfIneligibleCust: 0,
            numOfNotWebEnabledCust: 0,
            totalNumOfAccountHolders: 0,
            eauthInd: false,
            eAuthCustomerList: [],
        },
        transactionType: '',
        submitButtonsDisable: '',
        txnType: '',
    },
    anyAccountHolderIneligible: false,
    accountHolderIneligibleLog: false,
    useEauthCheckDisableLog: false,
    cashieringIsCustAuthRequired: false,
    payee: [],
    memo: '',
    requestedDate: '',
    isWithholdFedTax: false,
    isWithholdStateTax: false,
    cashieringIraAgreementText: '',
    assignToUserId: '',
    memoLine1CustomerId: 0,
    trustIncmPct: '',
    trustPrinPct: '',
    tranMemo: '',
    pendReasonsListSet: [],
    authResponse: {
      draftId: '',
      pendForRelease: false,
      pendForReleaseReason: '',
      canRelease: false
    },
    cashieringRequestId: 0,
    cashieringRequestSecondId: 0,
    revalidation: {
        status: '',
        errors: [],
        fieldsUpdated: []
     },
    mailAddressDetails: [],
    mailAddressTypeCode: '',
    isRmdEligible: false
}

export const standingAuthCheckReducer = createReducer(
    initialStandingAuthCheckInfoState,
    on(StandingAuthReviewPageActions.cancelClicked,
        StandingAuthFormActions.transactionTypeChanged,
        StandingAuthConfirmationPageActions.createNewStandingAuthClicked,
        () => {
            return initialStandingAuthCheckInfoState
        }),
    on(StandingAuthCheckActions.callOneTimeInput,
        CheckActions.callRecurringInput, (state) => {
            return {
                ...state,
                loading: true,
                inputCallSucceeded: false,
                inputCallFinished: false
            }
        }),
    on(StandingAuthCheckActions.inputCallSuccessful, (state, response) => {
        const { checkInputResponse } = response
        return {
            ...state,
            loading: false,
            error: '',
            hasError: false,
            inputCallSucceeded: true,
            inputCallFinished: true,
            defaultPayee: checkInputResponse?.cashieringCheckInputDetails?.cashieringPayeeDetails?.defaultPayee,
            stateList: checkInputResponse?.stateList,
            countryList: checkInputResponse?.countryList,
            checkReasonMap: mapCheckReasonsToList(checkInputResponse?.withdrawalReasonMap),
            defaultRecipient: checkInputResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.defaultRecipient,
            isAlternateInvestmentUsage: checkInputResponse?.isAlternateInvestmentUsage,
            alternateInvestmentText: checkInputResponse?.alternateInvestmentText,
            accountPlatformCode: checkInputResponse?.cashieringCheckInputDetails?.cashieringAccountRefDetails?.accountPlatformCode,
            recipient: checkInputResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails,
            isFirstPartyModified: checkInputResponse?.isFirstPartyModified,
            payeeDetails: checkInputResponse?.cashieringCheckInputDetails?.cashieringPayeeDetails?.payeeDetails,
            type: checkInputResponse?.type,
            useConstructedPayee: checkInputResponse?.cashieringCheckInputDetails?.cashieringPayeeDetails?.useConstructedPayee,
            processDateMM: checkInputResponse?.processDateMM,
            processDateDD: checkInputResponse?.processDateDD,
            processDateYYYY: checkInputResponse?.processDateYYYY,
            distributionReason: mapCheckDistributionReasonList(checkInputResponse.distributionReasonCode, checkInputResponse.distributionReasonCodeSubcode, checkInputResponse.distributionReasonsList),
            distributionReasonsList: checkInputResponse?.distributionReasonsList,
            stateTaxAmount: checkInputResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.iraTxn?.stateTaxAmount.toString(),
            stateTaxRate: (checkInputResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.iraTxn?.stateTaxRate * 100).toString(),
            defaultStateTaxRate: (checkInputResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.iraTxn?.stateTaxRate * 100).toString(),
            fedTaxAmount: checkInputResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.iraTxn?.fedTaxAmount.toString(),
            fedTaxRate: (checkInputResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.iraTxn?.fedTaxRate * 100).toString(),
            defaultFedTaxRate: (checkInputResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.iraTxn?.fedTaxRate * 100).toString(),
            stwProcessCode: checkInputResponse?.cashieringCheckInputDetails?.cashieringAccountRefDetails?.stwProcessCode as StwProcessCode,
            fedTaxAmountFormatted: checkInputResponse?.fedTaxAmountFormatted,
            stateTaxAmountFormatted: checkInputResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.stateTaxAmountFormatted,
            totalWithdrawalAmount: checkInputResponse?.totalWithdrawalAmount,
            stateTypeIndicator: checkInputResponse?.cashieringCheckInputDetails?.cashieringAccountRefDetails?.stateCd,
            stateCd: checkInputResponse?.stateCd,
            isNonNaState: checkInputResponse?.cashieringCheckInputDetails?.isNonNAState,
            deliveryMethodCode: checkInputResponse?.deliveryMethodCode,
            checkFeeDetails: checkInputResponse?.checkFeeDetails,
            shouldShowStateTax: hasStateTax(
                checkInputResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.nraAccountIndicator,
                checkInputResponse?.cashieringCheckInputDetails?.cashieringAccountRefDetails?.stwProcessCode
            ),
            cashieringIraAgreementText: checkInputResponse?.cashieringIraAgreementText,
            assignToUserId: checkInputResponse?.assignToUserId,
            memoLine1CustomerId: checkInputResponse?.memoLine1CustomerId,
            trustIncmPct: checkInputResponse?.trustIncmPct,
            trustPrinPct: checkInputResponse?.trustPrinPct,
            tranMemo: checkInputResponse?.tranMemo,
            isRmdEligible: checkInputResponse?.cashieringCheckInputDetails?.cashieringAccountRefDetails?.isRmdEligible
        };
    }),
    on(StandingAuthCheckActions.inputCallFailed, (state, err) => {
        return {
            ...state,
            loading: false,
            error: err.message,
            hasError: true,
            inputCallSucceeded: false,
            inputCallFinished: true
        }
    }),
    on(StandingAuthCheckActions.callCheckInquiry, (state, err) => {
        return {
            ...state,
            inquiryCallFinished: false
        }
    }),
    on(StandingAuthCheckActions.inquiryCallSuccessful, (state, response) => {
      let { checkInquiryResponse } = response;
      return {
        ...state,
        loading: false,
        inquiryCallFinished: true,
        distributionReason: mapCheckDistributionReasonList(checkInquiryResponse.distributionReasonCode, checkInquiryResponse.distributionReasonCodeSubcode, checkInquiryResponse.distributionReasonsList),
        distributionReasonsList: checkInquiryResponse?.distributionReasonsList,
        defaultStateTaxRate: (checkInquiryResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.stateTaxAmountFormatted),
        defaultFedTaxRate: (checkInquiryResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.fedTaxAmountFormatted),
        defaultRecipient: checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.defaultRecipient,
        recipient: checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails,
        payeeDetails: checkInquiryResponse?.cashieringCheckInputDetails?.cashieringPayeeDetails?.payeeDetails,
        defaultPayee: checkInquiryResponse?.cashieringCheckInputDetails?.cashieringPayeeDetails?.defaultPayee,
        checkReason: checkInquiryResponse.withdrawalReasonCode,
        memo: checkInquiryResponse.memo,
        checkReasonMap: mapCheckReasonsToList(checkInquiryResponse ?.withdrawalReasonMap),
        cashieringRequestId: checkInquiryResponse.cashieringReqId,
        cashieringRequestSecondId: checkInquiryResponse.cashieringReqSecondaryId,
        type: 'CheckSloa',
        countryList: checkInquiryResponse?.countryList,
        stateList: checkInquiryResponse?.stateList,
        cashieringIraAgreementText: checkInquiryResponse?.cashieringIraAgreementText,
        mailAddressDetails: checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.mailAddressDetails,
        mailAddressTypeCode: checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.mailAddressTypeCode
      }
    }),
    on(StandingAuthCheckActions.inquiryCallFailed, (state, err) => {
      return {
        ...state,
        loading: false,
        inquiryCallFinished: true,
        error: err.message
      }
    }),
    on(StandingAuthCheckActions.loadChecksReviewPageWithoutVerifyCall, (state, form) => {
        const details = form.standingAuthorizationDetails as CheckStandingAuthDetails;
        const today = new Date();
        return {
            ...state,
            loading: false,
            hasError: false,
            error: '',
            verifyCallFinished: true,
            serviceError: '',
            memo: details.checkMemo ?? '',
            deliveryAddress: getDeliveryAddressForNonVerifyCallFlow(details),
            payee: getPayeeNameForNonVerifyCallFlow(details),
            checkReason: details.checkReason ?? '',
            requestedDate: today.toLocaleDateString()
        }
    }),
    on(StandingAuthCheckActions.callVerify, (state) => {
        return {
            ...state,
            loading: true,
            hasError: false,
            error: '',
            verifyCallFinished: false,
            serviceError: ''
        }
    }),
    on(StandingAuthCheckActions.verifyCallSuccessful, (state, response) => {
        const { checkVerifyResponse } = response;

        return {
            ...state,
            loading: false,
            hasError: false,
            error: '',
            verifyCallFinished: true,
            fedTaxRate: checkVerifyResponse.fedTaxAmountFormatted,
            stateTaxRate: checkVerifyResponse.stateTaxAmountFormatted,
            shouldShowStateTax: hasStateTax(
                state?.nraAccountIndicator,
                state?.stwProcessCode
            ),
            clientAuthorizationNeeded: checkVerifyResponse.clientAuthorizationNeeded,
            eAuthModel: {
                ...checkVerifyResponse.eAuthModel,
                custAcctSLOAAuthorizationsList: checkVerifyResponse.eAuthModel.custAcctAuthorizationsSLOAList
            },
            displayClientAuthorizationSection: checkVerifyResponse.displayClientAuthorizationSection,
            deliveryAddress: state.initReleaseByDraftIdSucceeded && state.deliveryAddress.filter(item => item != "").length != 0 ? state.deliveryAddress : checkVerifyResponse.recipient,
            checkReason: checkVerifyResponse.withdrawalReasonDescription,
            isPendingForCTWE: checkVerifyResponse.isPendingForCTWE,
            isPendingOnlyForCTWE: checkVerifyResponse.isPendingOnlyForCTWE,
            anyAccountHolderIneligible: checkVerifyResponse.anyAccountHolderIneligible,
            accountHolderIneligibleLog: checkVerifyResponse.accountHolderIneligibleLog,
            useEauthCheckDisableLog: checkVerifyResponse.useEAuthCheckDisableLog,
            cashieringIsCustAuthRequired: checkVerifyResponse.cashieringIsCustAuthRequired,
            pendReasons: checkVerifyResponse.pendReasonsListSet,
            payee: checkVerifyResponse.payee,
            memo: checkVerifyResponse.memo,
            isWithholdFedTax: Number.parseFloat(checkVerifyResponse.fedTaxAmountFormatted) !== 0,
            isWithholdStateTax: Number.parseFloat(checkVerifyResponse.stateTaxAmountFormatted) !== 0,
            pendReasonsListSet: checkVerifyResponse.pendReasonsListSet,
            authResponse: checkVerifyResponse.authorizeAttributeResponse,
            revalidation: checkVerifyResponse.revalidation
        }
    }),
    on(StandingAuthCheckActions.acknowledgeCallSuccessful, (state, response) => {
      const { checkAcknowledgeResponse } = response;

      return {
        ...state,
        pendReasons: checkAcknowledgeResponse.pendReasonsListSet,
      }
    }),
    on(StandingAuthCheckActions.verifyCallFinishedWithError, (state, { checkVerifyResponse }) => {
        let errorObject = checkVerifyResponse.messageInfo.atsSystemErrors[0] || checkVerifyResponse.messageInfo.errorList[0];
        return {
            ...state,
            serviceError: errorObject.messageText,
            verifyCallFinished: false,
            revalidation: checkVerifyResponse.revalidation
        }
    }),
    on(StandingAuthCheckActions.acknowledgeCallSuccessful, (state, { checkAcknowledgeResponse }) => {
        return {
            ...state,
            requestedDate: checkAcknowledgeResponse.requestedDate
        }
    }),
    on(StandingAuthReviewPageActions.editClicked, (state) => {
        return {
            ...state,
            verifyCallFinished: false,
        };
    }),
    on(InitReleaseActions.initReleaseByDraftIdSuccessful, (state, { response, isSloa, isNraAccount }) => {
      if (!isSloa || response.transactionType !== 'check') return state;
      let draftObject = response.draftObject;
      return {
        ...state,
        initReleaseByDraftIdSucceeded: true,
        useConstructedPayee: !!draftObject.useConstructedPayee,
        defaultpayee: [
          draftObject.defaultPayee ? draftObject.defaultPayee[0] : ''
        ],
        cashieringRequestId: draftObject.cashieringReqId ?? 0,
        cashieringRequestSecondId: draftObject.cashieringSecondReqId ?? 0,
        accountPlatformCode: draftObject.accountPlatformCode ?? '',
        type: 'CheckSloa',
        recipient: {
          ...state.recipient,
          addressLine1: draftObject.recipient?.addressLine1 ?? '',
          addressLine2: draftObject.recipient?.addressLine2 ?? '',
          addressLine3: draftObject.recipient?.addressLine3 ?? '',
          city: draftObject.recipient?.city ?? '',
          stateName: draftObject.recipient?.stateName ?? '',
          stateCode: draftObject.recipient?.stateCode ?? '',
          zipCode: draftObject.recipient?.zipCode ?? '',
          countryCode: draftObject.recipient?.countryCode ?? '',
          countryName: draftObject.recipient?.countryName ?? ''
        },
        payeeDetails: {
          ...state.payeeDetails,
          payeeName1: draftObject.defaultPayee ? draftObject.defaultPayee[0] : '',
          payeeName2: draftObject.defaultPayee ? draftObject.defaultPayee[1] : '',
          payeeName3: draftObject.defaultPayee ? draftObject.defaultPayee[2] : '',
          customerId1: draftObject.payeeDetails?.customerId1 ?? 0,
          customerId2: draftObject.payeeDetails?.customerId2 ?? 0,
          customerId3: draftObject.payeeDetails?.customerId3 ?? 0
        },
        checkReasonMap: [ { description:  draftObject.withdrawalReason ?? '', code: draftObject.withdrawalReasonCode?.trim() ?? '' }],
        processDateMM: draftObject.processDateMM?.toString() ?? '',
        processDateDD: draftObject.processDateDD?.toString() ?? '',
        processDateYYYY: draftObject.processDateYYYY?.toString() ?? '',
        distributionReasonsList: [{
            code: draftObject.distributionReasonCd?.trim() ?? '',
            subCode: draftObject.distributionReasonSubCd?.trim() ?? '',
            description: draftObject.distributionReasonCdValue ?? ''
        }],
        fedTaxAmountFormatted: draftObject.fedTaxAmount.toString(),
        stateTaxAmountFormatted: draftObject.stateTaxAmount.toString(),
        stateTypeIndicator: draftObject.stateTypeInd ?? '',
        isNonNaState: !!draftObject.notNaState,
        checkFeeDetails: {
            ...state.checkFeeDetails,
            defaultFee: draftObject?.feeDetail?.DeliveryFee ?? 0,
            qualFee: draftObject?.feeDetail?.WaivedAmount ?? 0
        },
        stateCd: draftObject.stateTypeInd ?? '',
        assignToUserId: draftObject.assignToUserId ?? draftObject.AssignToUserId ?? '',
        memoLine1CustomerId: +(draftObject.memoLine1CustomerId ?? draftObject.MemoLine1CustomerId ?? 0),
        defaultRecipient: response.draftObject.sloaRecipient ?? [],
        trustIncmPct: draftObject.trustIncmPct,
        trustPrinPct: draftObject.trustPrinPct,
        tranMemo: draftObject.tranMemo,
        payee: [
            draftObject.isFirstPartyModified ? draftObject.payeeLine1 : `${draftObject.payeeDetails?.payeeName1 ? draftObject.payeeDetails?.payeeName1 : ''}
            ${draftObject.payeeLine2 ?? ''}
            ${draftObject.payeeLine3 ?? ''}`
          ],
        fedTaxRate: `${draftObject.fedTaxAmount.toString()}%`,
        deliveryAddress: getConcatenateDeliveryAddress(response),
        stwProcessCode: response.accountData.stwProcessCode as StwProcessCode ?? '',
        nraAccountIndicator: isNraAccount
      }
    })
)

function getDeliveryAddressForNonVerifyCallFlow(details: CheckStandingAuthDetails): string[] {
    let deliveryAddress: string[] = [];
      deliveryAddress.push(details.addresseeName ?? '');
      if(details.addresseeNameCont) {
        deliveryAddress.push(details.addresseeNameCont);
      }
      if(details.addresseeNameCont2) {
        deliveryAddress.push(details.addresseeNameCont2);
      }
      deliveryAddress.push(details.streetAddress ?? '');
      if (details.streetAddressCont) {
        deliveryAddress.push(details.streetAddressCont);
      }
      if (details.country) {
        deliveryAddress.push(`${details.city}, ${details.stateProvince ?? ''} ${details.postalCode}`);
        deliveryAddress.push(details.country);
      } else {
        deliveryAddress.push(`${details.city}, ${details.state} ${details.zipCode}`);
      }
      return deliveryAddress;
}

function getPayeeNameForNonVerifyCallFlow(details: CheckStandingAuthDetails): string[] {
    let payees: string[] = [details.payeeName!];
    if (details.additionalPayeeName1) {
      payees.push(details.additionalPayeeName1);
    }
    if (details.additionalPayeeName2) {
      payees.push(details.additionalPayeeName2);
    }
    return payees;
}

function hasStateTax(nraIndicator: boolean, stwProcessCode: StwProcessCode): boolean {
    if (nraIndicator) {
        return true;
    } else if (stwProcessCode) {
        return stwProcessCode !== 'N';
    } else {
        return false;
    }
}

function getConcatenateDeliveryAddress(response : DraftListAttributes){
    let deliveryAddress : string []=[];
    deliveryAddress.push(response.draftObject.otherDomesticmailTo1 ?? '');
    deliveryAddress.push(response.draftObject.otherDomesticmailTo2 ?? '');
    deliveryAddress.push(response.draftObject.otherDomesticmailTo3 ?? '');
    deliveryAddress.push(response.draftObject.otherDomesticstreetAddr1 ?? '');
    deliveryAddress.push(response.draftObject.otherDomesticstreetAddr2 ?? '');
    let domesticAddress=response.draftObject.otherDomesticAddrCity ?? '';
    domesticAddress=concatenateStrings(domesticAddress, response.draftObject.otherDomesticAddrState ?? '');
    deliveryAddress.push(`${domesticAddress ?? ''} ${response.draftObject.otherDomesticAddrZipCode ?? ''}`);
    deliveryAddress.push(response.draftObject.otherForeginmailTo1 ?? '');
    deliveryAddress.push(response.draftObject.otherForeginmailTo2 ?? '');
    deliveryAddress.push(response.draftObject.otherForeginmailTo3 ?? '');
    deliveryAddress.push(response.draftObject.otherForeginstreetAddr1 ?? '');
    deliveryAddress.push(response.draftObject.otherForeginstreetAddr2 ?? '');
    let foreignAddress=response.draftObject.otherForeginAddrCity ?? '';
    foreignAddress=concatenateStrings(foreignAddress, response.draftObject.otherForeginAddrState ?? '');
    deliveryAddress.push(`${foreignAddress ?? ''} ${response.draftObject.otherForegincAddrZipCode ?? ''}`);
    deliveryAddress.push(response.draftObject.otherForeginAddrCountry ?? '');

    return deliveryAddress;
}

function concatenateStrings(existingVal: string, newVal: string):string{
    if(newVal){
        return existingVal ? `${existingVal}, ${newVal}`: newVal;
    }

    return existingVal;

}

function mapCheckDistributionReasonList(distributionCode: string, distributionSubcode: string,  distributionReasonList: DistributionReason[]): DistributionReason {
    let distributionReasonCodeSubcode = distributionSubcode.split('-')[1];

    return distributionReasonList.find(reason => reason.code === distributionCode && reason.subCode.trim() === distributionReasonCodeSubcode.trim()) ?? new DistributionReason();
}
